import { getCompany } from "../../../../shared/data/storage/authStorage";
import { getCurrencySymbol } from "../../../../shared/utils/formatters";

/** 1 Thousand = 10^3 */
const THOUSAND = 1_000;
/** 1 Lakh = 10^5 */
const LAKH = 100_000;
/** 1 Million = 10^6 */
const MILLION = 1_000_000;
/** 1 Crore = 10^7 */
const CRORE = 10_000_000;
/** 1 Billion = 10^9 */
const BILLION = 1_000_000_000;

type AmountCompactUnit =
  | "whole"
  | "thousand"
  | "lakh"
  | "million"
  | "crore"
  | "billion";

type AmountCompactSuffixMap = Partial<Record<AmountCompactUnit, string>>;

function getCompanyAmountFormatConfig(): {
  unit: AmountCompactUnit;
  decimals: 1 | 2;
  suffixMap: AmountCompactSuffixMap;
  currencySymbol: string;
} {
  const company = getCompany();
  const currencyCode = (company?.currency ?? "INR").toUpperCase();

  const rawUnit = (company?.amountCompactUnit ?? "").toString().toLowerCase();
  const unit: AmountCompactUnit =
    rawUnit === "whole" ||
    rawUnit === "thousand" ||
    rawUnit === "lakh" ||
    rawUnit === "million" ||
    rawUnit === "crore" ||
    rawUnit === "billion"
      ? rawUnit
      : rawUnit === "crores"
        ? "crore"
        : rawUnit === "crors"
          ? "crore"
          : rawUnit === "lakhs"
            ? "lakh"
            : "lakh";

  const decimals: 1 | 2 =
    company?.amountCompactDecimals === 1 || company?.amountCompactDecimals === 2
      ? company.amountCompactDecimals
      : 2;

  const defaultSuffixMap: AmountCompactSuffixMap = {
    whole: "",
    thousand: "k",
    lakh: "L",
    million: "M",
    crore: "Cr",
    billion: "B",
  };

  let suffixMap: AmountCompactSuffixMap = defaultSuffixMap;
  const rawSuffixJson = company?.amountCompactSuffixJson;
  if (typeof rawSuffixJson === "string" && rawSuffixJson.trim()) {
    try {
      const parsed = JSON.parse(rawSuffixJson) as Record<string, unknown>;
      const merged: AmountCompactSuffixMap = { ...defaultSuffixMap };
      for (const k of Object.keys(parsed)) {
        const v = parsed[k];
        if (typeof v !== "string") continue;
        const key = k.toLowerCase();
        if (
          key === "whole" ||
          key === "thousand" ||
          key === "lakh" ||
          key === "million" ||
          key === "crore" ||
          key === "billion"
        ) {
          merged[key] = v;
        }
      }
      suffixMap = merged;
    } catch {
      suffixMap = defaultSuffixMap;
    }
  }

  const currencySymbol = getCurrencySymbol(currencyCode);
  return { unit, decimals, suffixMap, currencySymbol };
}

function unitDivisor(unit: AmountCompactUnit): number {
  switch (unit) {
    case "whole":
      return 1;
    case "thousand":
      return THOUSAND;
    case "lakh":
      return LAKH;
    case "million":
      return MILLION;
    case "crore":
      return CRORE;
    case "billion":
      return BILLION;
  }
}

function roundToDecimals(n: number, decimals: 1 | 2): number {
  if (!Number.isFinite(n)) return n;
  const factor = decimals === 1 ? 10 : 100;
  return Math.round((n + Number.EPSILON) * factor) / factor;
}

/**
 * Breakup formatter for Sub total / Tax total.
 * Uses the company compact unit (L/Cr/etc) but ROUNDS to configured decimals.
 *
 * This is intentionally different from `formatPlAmount` (which truncates),
 * to avoid "carry" mismatches when displaying breakups.
 */
export function formatBreakupAmount(amount: number | null | undefined): string {
  if (amount === null || amount === undefined) return "—";

  const { unit, decimals, suffixMap, currencySymbol } =
    getCompanyAmountFormatConfig();

  // Always compact to the company-selected unit so Sub/Tax are comparable
  // (avoid mixed output like "₹2.74L · ₹5,563.28").
  const divisor = unitDivisor(unit);
  const suffix = suffixMap[unit] ?? "";
  const scaled = amount / divisor;
  const roundedScaled = roundToDecimals(scaled, decimals);
  const neg = roundedScaled < 0;
  const s = Math.abs(roundedScaled).toFixed(decimals);
  return `${neg ? "-" : ""}${currencySymbol}${s}${suffix}`;
}
